import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import { Plus, Search, Filter, Edit, Trash2, Eye, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { vehiclesApi } from '../lib/api'
import { formatPrice, formatNumber, getStatusColor } from '../lib/utils'
import { useLanguage } from '../contexts/LanguageContext'

export default function Vehicles() {
  const [filters, setFilters] = useState({
    search: '',
    page: 1,
    limit: 10,
    sale: undefined,
    fuel: undefined,
    brand: undefined,
    model: undefined,
    color: undefined,
    min_price: undefined,
    max_price: undefined,
    min_mileage: undefined,
    max_mileage: undefined,
    year: undefined
  })
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [vehicleToDelete, setVehicleToDelete] = useState(null)

  const { currentLanguage } = useLanguage()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery(
    ['vehicles', filters],
    () => {
      // Filter out undefined values before sending to API
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
      )
      return vehiclesApi.getAll(cleanFilters)
    },
    {
      select: (data) => data.data,
      keepPreviousData: true,
      retry: 1,
      refetchOnWindowFocus: false
    }
  )

  const deleteMutation = useMutation(
    (id) => vehiclesApi.delete(id),
    {
      onSuccess: () => {
        toast.success('Vehicle deleted successfully')
        queryClient.invalidateQueries(['vehicles'])
        setDeleteModalOpen(false)
        setVehicleToDelete(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete vehicle')
      }
    }
  )

  const handleDeleteClick = (vehicle) => {
    setVehicleToDelete(vehicle)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (vehicleToDelete) {
      deleteMutation.mutate(vehicleToDelete.id)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false)
    setVehicleToDelete(null)
  }

  const handleFilterChange = (key, value) => {
    let processedValue = value
    
    // Convert string values to appropriate types for API
    if (value === '') {
      processedValue = undefined
    } else if (['fuel', 'year', 'model', 'brand', 'max_price', 'min_price', 'max_mileage', 'min_mileage', 'color'].includes(key)) {
      processedValue = value ? parseInt(value) : undefined
    } else if (key === 'sale') {
      processedValue = value === 'true' ? true : value === 'false' ? false : undefined
    }
    
    setFilters(prev => ({
      ...prev,
      [key]: processedValue,
      page: 1 // Reset to first page when filters change
    }))
  }

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }))
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading vehicles: {error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicles</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your vehicle inventory
          </p>
        </div>
        <Link to="/vehicles/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Vehicle
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="label">Search</label>
              <Input
                placeholder="Search vehicles..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            
            <div>
              <label className="label">Sale Status</label>
              <Select
                value={filters.sale === undefined ? '' : filters.sale.toString()}
                onChange={(e) => handleFilterChange('sale', e.target.value)}
              >
                <option value="">All</option>
                <option value="true">For Sale</option>
                <option value="false">Not For Sale</option>
              </Select>
            </div>

            <div>
              <label className="label">Year</label>
              <Input
                type="number"
                placeholder="2023"
                value={filters.year || ''}
                onChange={(e) => handleFilterChange('year', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Min Price</label>
              <Input
                type="number"
                placeholder="10000"
                value={filters.min_price || ''}
                onChange={(e) => handleFilterChange('min_price', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Max Price</label>
              <Input
                type="number"
                placeholder="50000"
                value={filters.max_price || ''}
                onChange={(e) => handleFilterChange('max_price', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Min Mileage</label>
              <Input
                type="number"
                placeholder="0"
                value={filters.min_mileage || ''}
                onChange={(e) => handleFilterChange('min_mileage', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Max Mileage</label>
              <Input
                type="number"
                placeholder="100000"
                value={filters.max_mileage || ''}
                onChange={(e) => handleFilterChange('max_mileage', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Fuel Type</label>
              <Input
                type="number"
                placeholder="Fuel Type ID"
                value={filters.fuel || ''}
                onChange={(e) => handleFilterChange('fuel', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Brand</label>
              <Input
                type="number"
                placeholder="Brand ID"
                value={filters.brand || ''}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Model</label>
              <Input
                type="number"
                placeholder="Model ID"
                value={filters.model || ''}
                onChange={(e) => handleFilterChange('model', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Color</label>
              <Input
                type="number"
                placeholder="Color ID"
                value={filters.color || ''}
                onChange={(e) => handleFilterChange('color', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vehicles Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Vehicles ({data?.count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : data?.results?.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[200px]">Vehicle</TableHead>
                      <TableHead className="min-w-[140px]">Brand & Model</TableHead>
                      <TableHead>Year</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead className="min-w-[140px]">Mileage</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.results.map((vehicle) => (
                      <TableRow key={vehicle.id}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            {vehicle.main_photo && vehicle.main_photo.length > 0 ? (
                              <img
                                className="h-12 w-12 rounded-lg object-cover"
                                src={import.meta.env.VITE_IMAGE_URL + vehicle.main_photo[0]}
                                alt={vehicle.vehicle_id}
                              />
                            ) : null}
                            <div>
                              <div className="font-medium">{vehicle.vehicle_id?.split('-')[0]}</div>
                              <div className="text-sm text-gray-500">{vehicle.engine}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {vehicle.model?.model_group?.manufacturer?.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {vehicle.model?.name}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{vehicle.year}</TableCell>
                        <TableCell className="font-medium">
                          {formatPrice(vehicle.price)}
                        </TableCell>
                        <TableCell>{formatNumber(vehicle.mileage)} km</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <Badge variant={getStatusColor(vehicle.status)}>
                              {vehicle.status}
                            </Badge>
                            {vehicle.sale && (
                              <Badge variant="success">For Sale</Badge>
                            )}
                            {vehicle.featured === 1 && (
                              <Badge variant="warning">Featured</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {/* <Link to={`/vehicles/${vehicle.id}`}>
                              <Button variant="ghost" size="sm">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Link> */}
                            <a href={`/vehicles/${vehicle.id}/edit`}>
                              <Button variant="ghost" size="sm">
                                <Edit className="h-4 w-4" />
                              </Button>
                            </a>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteClick(vehicle)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {data.pages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Showing {((filters.page - 1) * filters.limit) + 1} to{' '}
                    {Math.min(filters.page * filters.limit, data.count)} of{' '}
                    {data.count} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(filters.page - 1)}
                      disabled={filters.page === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-700">
                      Page {filters.page} of {data.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(filters.page + 1)}
                      disabled={filters.page === data.pages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No vehicles</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first vehicle.
              </p>
              <div className="mt-6">
                <Link to="/vehicles/new">
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Vehicle
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete Vehicle</h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleDeleteCancel}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete this vehicle? This action cannot be undone.
              </p>
              {vehicleToDelete && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">{vehicleToDelete.vehicle_id}</p>
                  <p className="text-sm text-gray-600">
                    {vehicleToDelete.model?.model_group?.manufacturer?.name} - {vehicleToDelete.model?.name}
                  </p>
                  <p className="text-sm text-gray-600">{vehicleToDelete.year}</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleDeleteCancel}
                disabled={deleteMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteConfirm}
                loading={deleteMutation.isLoading}
              >
                Delete Vehicle
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
